/**
 * settingsSchemas.js — per-widget settings declarations.
 *
 * Each schema is an array of fields:
 *   { key, label, type, options?, min?, max?, step?, placeholder?, help? }
 *
 * Field types:
 *   - 'number'    — numeric input with optional min/max/step
 *   - 'text'      — single-line text input
 *   - 'select'    — dropdown; options is [{ value, label }]
 *   - 'toggle'    — boolean checkbox
 *
 * Field flags:
 *   - `adminOnly: 'spy'` — only rendered when the viewer is an admin
 *     who is currently impersonating (Spy Mode, issue #133). Hidden
 *     for everyone else; the value is still persisted on the widget
 *     so admins keep their config across spy sessions.
 *
 * Settings live on the widget row (`widget.settings`) and persist via
 * wm.setSettings → debounced PATCH (logged-in) or localStorage (anon).
 */

const SPY_GUILD_FILTER = {
  key: 'spyGuildFilter',
  label: 'Spy: guild filter',
  type: 'select',
  adminOnly: 'spy',
  options: [
    { value: 'all',      label: 'All guilds' },
    { value: 'known',    label: 'Known guilds (named, has data)' },
    { value: 'unknown',  label: 'Unknown guilds (no name / “Unknown”)' },
    { value: 'no-data',  label: 'Guilds with no data' },
  ],
  help: 'Admin-only spy-mode filter. Mirrors the v1 guild dropdown grouping so an admin auditing a target can isolate known, unknown, or empty guilds. Only applied while you are spying on a user.',
};

const WEEK_OPTIONS = [
  { value: 0, label: 'This week' },
  { value: 1, label: 'Last week' },
  { value: 2, label: '2 weeks ago' },
  { value: 3, label: '3 weeks ago' },
  { value: 4, label: '4 weeks ago' },
];

const MY_SALES_WEEK_OPTIONS = [
  { value: -1, label: 'All time' },
  ...WEEK_OPTIONS,
];

const ESO_QUALITY_OPTIONS = [
  { value: '', label: 'Any quality' },
  { value: 'white', label: 'White' },
  { value: 'green', label: 'Green' },
  { value: 'blue', label: 'Blue' },
  { value: 'purple', label: 'Purple' },
  { value: 'gold', label: 'Gold' },
];

const ESO_TRAIT_OPTIONS = [
  { value: '', label: 'Any trait' },
  { value: 'arcane', label: 'Arcane' },
  { value: 'bloodthirsty', label: 'Bloodthirsty' },
  { value: 'charged', label: 'Charged' },
  { value: 'decisive', label: 'Decisive' },
  { value: 'defending', label: 'Defending' },
  { value: 'divines', label: 'Divines' },
  { value: 'harmony', label: 'Harmony' },
  { value: 'healthy', label: 'Healthy' },
  { value: 'impenetrable', label: 'Impenetrable' },
  { value: 'infused', label: 'Infused' },
  { value: 'intricate', label: 'Intricate' },
  { value: 'invigorating', label: 'Invigorating' },
  { value: 'nirnhoned', label: 'Nirnhoned' },
  { value: 'ornate', label: 'Ornate' },
  { value: 'powered', label: 'Powered' },
  { value: 'precise', label: 'Precise' },
  { value: 'protective', label: 'Protective' },
  { value: 'reinforced', label: 'Reinforced' },
  { value: 'robust', label: 'Robust' },
  { value: 'sharpened', label: 'Sharpened' },
  { value: 'sturdy', label: 'Sturdy' },
  { value: 'swift', label: 'Swift' },
  { value: 'training', label: 'Training' },
  { value: 'triune', label: 'Triune' },
  { value: 'well-fitted', label: 'Well-Fitted' },
];

const SCHEMAS = {
  'relay-operations': [
    { key: 'inactiveDays', label: 'Inactive after', type: 'number', min: 1, max: 180, step: 1,
      help: 'Accounts with no completed sync inside this many days are marked inactive.' },
  ],
  'leaderboard': [
    { key: 'metric', label: 'Rank by', type: 'select', options: [
      { value: 'gold',  label: 'Total gold' },
      { value: 'sales', label: 'Sales count' },
    ], help: 'Which column drives the ranking.' },
    { key: 'guild', label: 'Guild filter', type: 'select', options: [
      { value: 'all', label: 'All guilds' },
      { value: 'post', label: 'Trading Post' },
      { value: 'caravan', label: 'Trading Caravan' },
      { value: 'dealers', label: 'Dealers' },
    ], help: 'Show leaderboard totals for one Redfur guild or across all guilds.' },
    { key: 'weekOffset', label: 'Starting trade week', type: 'select', options: [
      { value: 0,    label: 'This week' },
      { value: 1,    label: 'Last week' },
      { value: 2,    label: '2 weeks ago' },
      { value: 3,    label: '3 weeks ago' },
      { value: 4,    label: '4 weeks ago' },
      { value: -1,   label: 'All time' },
    ], help: 'Which week the leaderboard opens to. Use the header arrows or center button to jump to any other week or a custom date range.' },
    { key: 'limit', label: 'Top sellers shown', type: 'number', min: 5, max: 100, step: 5, scopable: true,
      help: 'How many sellers to display. The windowed and maximized views can carry different values — keep windowed tight (top 10) and maximized deep (top 100).' },
  ],
  'recent-sales': [
    { key: 'mode',    label: 'Show sales from', type: 'select', options: [
      { value: 'all',  label: 'Everyone' },
      { value: 'mine', label: 'Only me' },
    ], help: '“Only me” auto-fills your @-tag and ignores the Seller filter below.' },
    { key: 'limit',   label: 'Rows shown',   type: 'number', min: 10, max: 200, step: 10, scopable: true,
      help: 'How many rows to render. The windowed and maximized views can carry different values — keep windowed compact and maximized deeper.' },
    { key: 'item',    label: 'Item name', type: 'text', placeholder: 'Dreugh Wax' },
    { key: 'seller',  label: 'Seller @-tag', type: 'text',   placeholder: '@username',
      help: 'Ignored when “Show sales from” is set to “Only me”.' },
    { key: 'guildId', label: 'Guild ID',     type: 'text',   placeholder: 'numeric guild id (leave blank for all)' },
    SPY_GUILD_FILTER,
  ],
  'my-sales': [
    { key: 'weekOffset', label: 'Week', type: 'select', options: MY_SALES_WEEK_OPTIONS,
      help: 'Single-week view. Ignored when From/To dates or a week range are set.' },
    // Issue #110 — explicit ranges + live mode.
    { key: 'dateFrom', label: 'From date', type: 'date',
      help: 'Optional. When both From/To are set the widget shows every sale in that span.' },
    { key: 'dateTo',   label: 'To date',   type: 'date' },
    { key: 'weekFrom', label: 'Week range · oldest', type: 'select', options: WEEK_OPTIONS,
      help: 'Optional trade-week range (used only if From/To dates are blank).' },
    { key: 'weekTo',   label: 'Week range · newest', type: 'select', options: WEEK_OPTIONS },
    { key: 'live', label: 'Live mode', type: 'toggle', default: false,
      help: 'Subscribe to the live sales SSE stream and refresh the moment a new sale matching your @-tag arrives.' },
  ],
  'sync-points': [
    { key: 'limit', label: 'Sync sessions shown', type: 'number', min: 5, max: 100, step: 5,
      help: 'How many recent upload sessions Your Sync Data can search and display.' },
  ],
  'guild-stats': [
    { key: 'guildId',    label: 'Default guild', type: 'text', placeholder: 'leave blank for all Redfur' },
    { key: 'weekOffset', label: 'Starting week', type: 'select', options: WEEK_OPTIONS },
  ],
  'raffle': [
    { key: 'guild', label: 'Guild', type: 'select', options: [
      { value: 'both',    label: 'Both guilds' },
      { value: 'post',    label: 'Trading Post' },
      { value: 'dealers', label: 'Dealers' },
    ] },
    { key: 'limit', label: 'Show top', type: 'number', min: 5, max: 50, step: 5 },
  ],
  'my-raffle': [
    { key: 'guilds', label: 'Tracked guilds', type: 'multi-toggle', options: [
      { value: 'post',    label: 'Trading Post' },
      { value: 'dealers', label: 'Dealers' },
    ], help: 'Show a card for each selected guild. Defaults to all if none picked.' },
    { key: 'weekOffset', label: 'Week', type: 'select', options: WEEK_OPTIONS },
  ],
  'price-check': [
    { key: 'mode', label: 'Price source',  type: 'select', options: [
      { value: 'all',   label: 'All sources' },
      { value: 'mm',    label: 'Mostly Marketplace (guild sales)' },
      { value: 'ttc',   label: 'Tamriel Trade Centre' },
      { value: 'ghost', label: 'Ghost / TTC-only items' },
    ], help: 'Restrict results to items that have data from this source.' },
    { key: 'sort', label: 'Sort by', type: 'select', options: [
      { value: 'relevance',  label: 'Relevance' },
      { value: 'recent',     label: 'Recently sold' },
      { value: 'price-desc', label: 'Highest price' },
      { value: 'price-asc',  label: 'Lowest price' },
      { value: 'name',       label: 'Name (A→Z)' },
    ] },
    { key: 'quality', label: 'Quality', type: 'select', options: ESO_QUALITY_OPTIONS,
      help: 'Default ESO quality filter used by the inline Price Check filter bar.' },
    { key: 'trait', label: 'Trait', type: 'select', options: ESO_TRAIT_OPTIONS,
      help: 'Default ESO trait filter used by the inline Price Check filter bar.' },
    { key: 'q', label: 'Default search', type: 'text', placeholder: 'item name fragment' },
  ],
  'eso-clock': [
    { key: 'clockTheme', label: 'Clock theme', type: 'select', options: [
      { value: '', label: 'Current desktop theme' },
      { value: 'moonlit', label: 'Moonlit blue' },
      { value: 'dawn', label: 'Dawn gold' },
      { value: 'bloodmoon', label: 'Bloodmoon' },
      { value: 'apocrypha', label: 'Apocrypha green' },
    ], help: 'Overrides only the ESO Clock scene; leave on Current to inherit the active desktop theme.' },
    { key: 'moonPhaseMode', label: 'Moon cycles', type: 'select', options: [
      { value: 'in-game', label: 'In-game synced' },
      { value: 'lore', label: 'Lore split cycles' },
    ], help: 'In-game mode mirrors ESO. Lore mode lets Jode and Jone use independent cycle math.' },
    { key: 'animateMoons', label: 'Animate moons', type: 'toggle', default: true,
      help: 'Uses CSS-only drift and shimmer animations. Disable for a still clock.' },
    { key: 'showMoons', label: 'Show moon scene', type: 'toggle', default: true },
    { key: 'showDate', label: 'Show ESO date', type: 'toggle', default: true },
    { key: 'showPhase', label: 'Show phase cycle', type: 'toggle', default: true },
    { key: 'showEarthMoon', label: 'Show Earth moon chip', type: 'toggle', default: true },
    { key: 'showLoreToggle', label: 'Show moon-mode button', type: 'toggle', default: true },
    { key: 'showCommentary', label: 'Show Fissal note', type: 'toggle', default: true },
  ],
  'live-feed': [
    { key: 'showSales',    label: 'Show sales',     type: 'toggle', default: true, scopable: true },
    { key: 'showBigSales', label: 'Show big sales', type: 'toggle', default: true, scopable: true,
      help: 'Sales whose price meets the threshold below.' },
    { key: 'bigSaleThreshold', label: 'Big-sale threshold', type: 'number',
      min: 0, max: 100000000, step: 5000, default: 50000,
      help: 'Price (gold) at which a sale is highlighted as 🔥 big.' },
    { key: 'showSyncs',  label: 'Show syncs',  type: 'toggle', default: true, scopable: true },
    { key: 'showPoints', label: 'Show points', type: 'toggle', default: true, scopable: true,
      help: 'Sync-point award events (when available).' },
    // Issue #109 — per-row column toggles. Lets the user collapse a feed
    // row to just the fields they actually care about so narrow windows
    // stay readable.
    { key: 'fieldItem',   label: 'Item',   type: 'toggle', default: true },
    { key: 'groupSalesByItem', label: 'Group sales by item', type: 'toggle', default: true, scopable: true,
      showWhen: { key: 'fieldItem', equals: true },
      help: 'Legacy-style view: combine multiple seller rows under each recently sold item.' },
    { key: 'fieldSeller', label: 'Seller', type: 'toggle', default: true },
    { key: 'fieldGuild',  label: 'Guild',  type: 'toggle', default: true },
    { key: 'fieldPrice',  label: 'Price',  type: 'toggle', default: true },
    { key: 'fieldTime',   label: 'Time',   type: 'toggle', default: true,
      help: 'Relative timestamp (5m ago, 2h ago, …) — hover for the exact datetime.' },
    { key: 'limit',   label: 'Rows shown',    type: 'number', min: 10, max: 300, step: 10, scopable: true,
      help: 'How many rows to render. The windowed and maximized views can carry different values — keep windowed compact and maximized deeper.' },
    { key: 'item',    label: 'Item name', type: 'text', placeholder: 'Dreugh Wax' },
    { key: 'seller',  label: 'Seller @-tag',  type: 'text',   placeholder: '@username' },
    { key: 'guildId', label: 'Guild', type: 'select', options: [
      { value: '',       label: 'All Redfur guilds' },
      { value: '788815', label: '⚔ Post' },
      { value: '822467', label: '🐎 Caravan' },
      { value: '865409', label: '💰 Dealers' },
    ] },
    SPY_GUILD_FILTER,
  ],
  'item-detail': [
    { key: 'name',    label: 'Item name', type: 'text', placeholder: 'e.g. Perfect Roe' },
    { key: 'quality', label: 'Quality', type: 'select', options: [
      { value: '',          label: 'Any' },
      { value: 'normal',    label: 'Normal' },
      { value: 'fine',      label: 'Fine' },
      { value: 'superior',  label: 'Superior' },
      { value: 'epic',      label: 'Epic' },
      { value: 'legendary', label: 'Legendary' },
    ] },
    { key: 'trait', label: 'Trait', type: 'text', placeholder: 'e.g. Divines' },
  ],
  'user-detail': [
    { key: 'tag', label: 'Default @-tag', type: 'text', placeholder: '@name', adminOnly: true },
    { key: 'weekOffset', label: 'Trade week', type: 'select', options: WEEK_OPTIONS,
      help: 'Default trade week shown in the rich user profile.' },
    SPY_GUILD_FILTER,
  ],
  'kiosk-tracking': [
    { key: 'zoneFilter', label: 'Default zone search', type: 'text',
      placeholder: 'e.g. wayrest', help: 'Pre-fills the zone search box inside the widget.' },
    { key: 'guildFilter', label: 'Default guild search', type: 'text',
      placeholder: 'e.g. Redfur', help: 'Pre-fills the guild search box inside the widget.' },
    { key: 'showOnlyMatchingGuild', label: 'Only show zones held by the filtered guild',
      type: 'toggle', scopable: true,
      help: 'When set on the windowed view, the small layout stays focused on one guild while the maximized view can keep showing everything.' },
  ],
  'trader-bid-history': [
    { key: 'weekOffset', label: 'Default week', type: 'number', min: 0, max: 59, step: 1,
      help: '0 = latest available week. Increase to open to an earlier week by default.' },
    { key: 'zoneFilter', label: 'Default zone filter', type: 'text', placeholder: 'e.g. Wayrest',
      help: 'Pre-fills the zone search box.' },
  ],
  // Claim @-tag form has no persisted settings — the user value is the
  // claim token kept in localStorage by the widget itself. Registered as
  // an empty schema so the catalog knows the widget exists and the gear
  // icon stays hidden (hasSchema returns false for empty arrays).
  'claim-tag': [],
  // Settings widget owns its own internal tab state — no per-instance
  // schema. Empty array hides the gear icon (it would just be a settings
  // panel-of-settings, which is silly).
  'settings': [],
  'kiosk-zone-history': [
    { key: 'zone', label: 'Zone / town', type: 'text',
      placeholder: 'e.g. Wayrest',
      help: 'Filter the timeline to a single trader zone or town. Combine with guild/trader for tighter searches.' },
    { key: 'guildName', label: 'Guild name (partial match)', type: 'text',
      placeholder: 'e.g. Redfur, Hodorhood',
      help: 'Tracks ANY guild — Redfur or not — across the kiosk timeline. Partial match (LIKE %name%).' },
    { key: 'trader', label: 'Trader NPC name', type: 'text',
      placeholder: 'e.g. Pacrooti',
      help: 'Filter by the kiosk’s NPC trader name (community spreadsheet only).' },
    { key: 'limit', label: 'Rows shown', type: 'number',
      min: 10, max: 200, step: 10,
      help: 'How many rows to render. Default 50.' },
  ],
  'kiosks': [
    { key: 'view', label: 'Default view', type: 'select', options: [
      { value: 'guilds', label: 'By Guild' },
      { value: 'zones', label: 'By Zone / NPC' },
    ] },
    { key: 'weekOffset', label: 'Default week', type: 'number', min: 0, max: 59, step: 1,
      help: '0 = latest available trader week.' },
    { key: 'search', label: 'Default search', type: 'text', placeholder: 'Guild, zone, or NPC' },
    { key: 'guildName', label: 'Default guild filter', type: 'text', placeholder: 'e.g. Redfur' },
    { key: 'zone', label: 'Default zone/city filter', type: 'text', placeholder: 'e.g. Wayrest' },
    { key: 'trader', label: 'Default NPC filter', type: 'text', placeholder: 'e.g. Pacrooti' },
    { key: 'limit', label: 'Rows shown', type: 'number', min: 10, max: 300, step: 10 },
  ],
  'storage-cleanup': [
    { key: 'showFiles', label: 'Show file list', type: 'toggle',
      help: 'Display the oldest charts on disk so you can spot bloat by name.' },
    { key: 'fileLimit', label: 'Files shown', type: 'number',
      min: 5, max: 200, step: 5,
      help: 'Maximum number of files to list when "Show file list" is on.' },
  ],
  'trader-persona': [
    // No user-facing settings. Persona note is edited inline in the widget.
  ],
  'admin-sales':  [],
  'redfur-audit': [],
  'auditing': [
    { key: 'view', label: 'Default view', type: 'select', options: [
      { value: 'sales', label: 'Sales' },
      { value: 'players', label: 'Players' },
      { value: 'guilds', label: 'Guilds' },
    ] },
  ],
  'admin-raffle': [],
  'admin-tools':     [],
  'tone-generator':  [],
  'admin-memories':  [],
  'system':          [],
  'sprint-runner':   [],
  'fissal-prime':    [],
  'discord-console': [],
  'api-docs':        [],
  'immich':            [],
  'open-webui':        [],
  'fissal-open-webui': [],
  'frigate':           [],
  'family-devices':    [],
  'homelab-overview':  [],
  'horus-tools':       [],
  'osint-research':     [],
};

// Mirrors src/web/routes/v2/widgets/_aliases.js — keeps schema lookups
// working for instances still flagged with a legacy widget_type slug.
const SCHEMA_ALIASES = {
  'player-insight':     'user-detail',
  'profile-management': 'user-detail',
  'guild-tracking':     'auditing',
  'sync-status':        'relay-operations',
  'server-perf':        'system',
  'recent-errors':      'system',
  'guild-audit':     'redfur-audit',
  'player-tracking': 'member-auditing',
  'item-analysis':   'item-detail',
  'sync-history':    'sync-points',
  'weekly-sales':    'my-sales',
  'kiosk-tracking':     'kiosks',
  'kiosk-zone-history': 'kiosks',
  'trader-bid-history': 'kiosks',
  'fissal-ai':       'fissal-open-webui',
  'horus-tools':     'osint-research',
};

export function getSchema(widgetType) {
  if (SCHEMAS[widgetType]) return SCHEMAS[widgetType];
  const aliased = SCHEMA_ALIASES[widgetType];
  return (aliased && SCHEMAS[aliased]) || SCHEMAS[widgetType] || null;
}

// Field-visibility gate (#133): admin-only fields are hidden unless
// the gate matches. Mirrors the logic in SettingsPanel so callers can
// decide whether the ⚙ button is even worth showing.
export function isFieldVisible(field, identity) {
  if (!field?.adminOnly) return true;
  if (!identity?.isAdmin) return false;
  if (field.adminOnly === 'spy') return !!identity.impersonating;
  return true;
}

export function hasSchema(widgetType, identity) {
  const s = getSchema(widgetType);
  if (!Array.isArray(s) || s.length === 0) return false;
  if (!identity) return s.some(f => !f.adminOnly);
  return s.some(f => isFieldVisible(f, identity));
}
